// @givmo/org-console — the Profile page's QR design page (PE-WEB11): where the Public tab's QR card's
// "Customize" lands. It frames the ONE shared editor, `OrgQrDesignEditor`, on the organization's
// PUBLIC code, so the preview is the code people scan to reach the organization's public page (or
// whatever the host says its public code leads to).
//
// Rendering that code takes `CAPABILITY_MANAGE_ORG_QR`, so a person without it has nothing to style
// here: the page says so rather than open an editor whose every render the server would refuse.
//
// ⚠ IT DECIDES OFF THE HOST'S ONE STANDING READ, AND RESOLVES IN EVERY STATE THAT READ CAN END IN.
// `capabilities` is `null` both while that read is in flight and after it ENDED without reporting any —
// a failed read, or the console's route miss — so `null` alone cannot mean "wait". The host says where
// the read stands (`standing`): in flight, the page waits; failed, the package's one retry
// (`OrgTierUnknownNotice`); settled with nothing reported, the calm not-available notice. Neither ended
// state keeps the subtitle, which describes an editor that is not there.

import { Button, Icon, PageHeader, Skeleton, testIdProp } from '@givmo/design-system';
import { ArrowLeft } from 'lucide-react';
import type { ModuleReadState } from './module-gate';
import {
  OrgConsoleUnavailableNotice,
  OrgNotPermittedNotice,
  OrgTierUnknownNotice,
} from './org-console-notices';
import { OrgQrDesignEditor, type OrgQrDesignVocab } from './org-qr-design-editor';
import { CAPABILITY_MANAGE_ORG_QR, holdsCapability } from './standing-read';

export const PROFILE_QR_TEST_ID = 'page-profile-qr';

export interface OrgProfileQrPageProps {
  orgRef: string;
  /** The standing read's capabilities; `null` while not reported. */
  capabilities: readonly string[] | null;
  /**
   * Where that same read stands — the host's own signals, never a read this page opens. It decides
   * what renders while `capabilities` is `null`: in flight, a wait; failed, the retry; otherwise the
   * not-available notice.
   */
  standing: Pick<ModuleReadState, 'pending' | 'errored'>;
  /** Re-run the standing read — offered with the failed read's notice. */
  onRetry: () => void;
  vocab: OrgQrDesignVocab;
  /** What the preview is, in the host's words — "your public page’s code". */
  previewNoun: string;
  /** The organization's logo for the editor's logo choice — the host already reads it. */
  orgLogoSrc?: string | null;
  /** Back to the Profile page. */
  onBack: () => void;
  testIdPrefix: string;
}

export function OrgProfileQrPage({
  orgRef,
  capabilities,
  standing,
  onRetry,
  vocab,
  previewNoun,
  orgLogoSrc = null,
  onBack,
  testIdPrefix,
}: OrgProfileQrPageProps) {
  const canSave = holdsCapability(capabilities, CAPABILITY_MANAGE_ORG_QR);
  // The read ended without reporting any capabilities: nothing here is left to wait for.
  const ended = capabilities === null && !standing.pending;
  return (
    <div
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-20 px-16 py-24 sm:px-24"
      {...testIdProp(PROFILE_QR_TEST_ID)}
    >
      <div>
        <Button
          appearance="saas"
          tone="ghost"
          size="sm"
          leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
          className="max-lg:min-h-44"
          onClick={onBack}
          testId={`${testIdPrefix}-qr-back`}
        >
          Profile
        </Button>
      </div>
      <PageHeader
        title="QR code design"
        subtitle={
          ended
            ? undefined
            : `Choose how ${vocab.orgName} QR codes look. The preview is ${previewNoun}.`
        }
      />
      {capabilities === null ? (
        !ended ? (
          <Skeleton className="h-[320px] w-full rounded-xl" testId={`${testIdPrefix}-qr-waiting`} />
        ) : standing.errored ? (
          <OrgTierUnknownNotice testId={`${testIdPrefix}-qr-standing-retry`} onRetry={onRetry} />
        ) : (
          <OrgConsoleUnavailableNotice
            testId={`${testIdPrefix}-qr-unavailable`}
            title="QR code design"
            description="QR code design isn’t available for this organization here."
          />
        )
      ) : canSave ? (
        <OrgQrDesignEditor
          orgRef={orgRef}
          target={{ kind: 'public' }}
          canSave
          vocab={vocab}
          orgLogoSrc={orgLogoSrc}
          testIdPrefix={testIdPrefix}
        />
      ) : (
        <OrgNotPermittedNotice
          testId={`${testIdPrefix}-qr-locked`}
          title="QR code design"
          description={
            vocab.saveUnavailable ??
            'Changing your organization’s QR code design isn’t available to you.'
          }
        />
      )}
    </div>
  );
}
